// src/pages/PanelFirma/ContextoExpediente.js

import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, CircularProgress, Divider, List,
    ListItem, ListItemText, Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
    ExpandMore as ExpandMoreIcon,
    Folder as FolderIcon,
    People as PeopleIcon,
    Gavel as GavelIcon,
    History as HistoryIcon,
} from '@mui/icons-material';

import { panelFirmaContextoJWT } from '../../api/endpoints';

const styles = {
    root: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    loadingBox: {
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: 300,
    },
    section: {
        padding: '8px',
        borderRadius: '4px',
    },
    sectionTitle: {
        fontWeight: 600,
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        fontSize: '0.85rem',
    },
    infoRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '1px 0',
    },
    label: {
        color: 'rgba(0, 0, 0, 0.6)',
        fontSize: '0.78rem',
        flexShrink: 0,
        marginRight: '8px',
    },
    value: {
        fontWeight: 500,
        fontSize: '0.82rem',
        textAlign: 'right',
        maxWidth: '65%',
        wordBreak: 'break-word',
    },
    accordion: {
        '&:before': { display: 'none' },
        boxShadow: 'none',
        border: '1px solid rgba(0,0,0,0.12)',
        borderRadius: '4px !important',
        marginBottom: '4px',
    },
    accordionSummary: {
        minHeight: '32px !important',
        '& .MuiAccordionSummary-content': {
            margin: '4px 0 !important',
        },
    },
    movItem: {
        paddingTop: '2px',
        paddingBottom: '2px',
    },
    caracterHeader: {
        fontWeight: 600,
        fontSize: '0.72rem',
        color: 'rgba(0, 0, 0, 0.6)',
        marginTop: '4px',
        marginBottom: '2px',
        display: 'block',
    },
    parteNombre: {
        fontSize: '0.82rem',
        lineHeight: 1.3,
    },
    parteExtra: {
        fontSize: '0.7rem',
    },
};

// Agrupar partes por Caracter
const groupByCaracter = (partes) => {
    const groups = {};
    partes.forEach(p => {
        const key = p.Caracter || 'Sin carácter';
        if (!groups[key]) groups[key] = [];
        groups[key].push(p);
    });
    return groups;
};

const ContextoExpediente = ({ idExpediente }) => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);

    useEffect(() => {
        if (!idExpediente) return;
        setLoading(true);
        panelFirmaContextoJWT(idExpediente)
            .then(resp => {
                if (resp?.ok) setData(resp);
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, [idExpediente]);

    if (loading) {
        return <Box sx={styles.loadingBox}><CircularProgress size={32} /></Box>;
    }

    if (!data) {
        return (
            <Paper sx={styles.section}>
                <Typography variant="body2" color="text.secondary">No se pudo cargar el contexto del expediente</Typography>
            </Paper>
        );
    }

    const exp = data.expediente || {};
    const partes = data.partes || [];
    const radicacion = data.radicacion || {};
    const movimientos = data.movimientos || [];

    // Agrupar partes por Caracter
    const partesAgrupadas = groupByCaracter(partes);

    // Radicación compuesta
    const radicacionCompleta = [radicacion.Juzgado, radicacion.Fuero, radicacion.Jurisdiccion]
        .filter(Boolean).join(' - ');

    return (
        <Box sx={styles.root}>
            {/* Expediente Info */}
            <Paper sx={styles.section}>
                <Typography sx={styles.sectionTitle} style={{ marginBottom: 4 }}>
                    <FolderIcon fontSize="small" color="primary" /> Expediente
                </Typography>
                <Divider style={{ marginBottom: 3 }} />
                <Box sx={styles.infoRow}>
                    <Typography sx={styles.label}>Carátula</Typography>
                    <Typography sx={styles.value}>{exp.Caratula || '-'}</Typography>
                </Box>
                <Box sx={styles.infoRow}>
                    <Typography sx={styles.label}>Autos</Typography>
                    <Typography sx={styles.value}>{exp.Autos || '-'}</Typography>
                </Box>
                <Box sx={styles.infoRow}>
                    <Typography sx={styles.label}>Estado Procesal</Typography>
                    <Typography sx={styles.value}>{exp.EstadoProcesal || '-'}</Typography>
                </Box>
                {exp.Expediente_Adm && (
                    <Box sx={styles.infoRow}>
                        <Typography sx={styles.label}>Expediente Adm.</Typography>
                        <Typography sx={styles.value}>{exp.Expediente_Adm}</Typography>
                    </Box>
                )}
            </Paper>

            {/* Partes agrupadas por Carácter */}
            <Accordion sx={styles.accordion} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                    <Typography sx={styles.sectionTitle}>
                        <PeopleIcon fontSize="small" color="primary" /> Partes ({partes.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: '0 12px 8px', display: 'block' }}>
                    {Object.keys(partesAgrupadas).length === 0 ? (
                        <Typography variant="caption" color="text.secondary">Sin partes registradas</Typography>
                    ) : Object.entries(partesAgrupadas).map(([caracter, personas], gi) => (
                        <Box key={gi}>
                            <Typography sx={styles.caracterHeader}>
                                {caracter.toUpperCase()} ({personas.length})
                            </Typography>
                            {personas.map((p, i) => (
                                <Box key={i} style={{ paddingLeft: 8, marginBottom: 2 }}>
                                    <Typography sx={styles.parteNombre}>{p.Persona || '-'}</Typography>
                                    <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
                                        {p.Matricula && (
                                            <Typography sx={styles.parteExtra} color="text.secondary">
                                                Mat: {p.Matricula}
                                            </Typography>
                                        )}
                                        {p.Carta_Poder && (
                                            <Typography sx={styles.parteExtra} color="primary">
                                                CP: {p.Carta_Poder}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            ))}
                        </Box>
                    ))}
                </AccordionDetails>
            </Accordion>

            {/* Radicacion */}
            {radicacionCompleta && (
                <Accordion sx={styles.accordion} defaultExpanded>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                        <Typography sx={styles.sectionTitle}>
                            <GavelIcon fontSize="small" color="primary" /> Radicación
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails style={{ padding: '0 12px 6px' }}>
                        <Box sx={styles.infoRow}>
                            <Typography sx={styles.label}>Juzgado</Typography>
                            <Typography sx={styles.value}>{radicacion.Juzgado || '-'}</Typography>
                        </Box>
                        {radicacion.Fuero && (
                            <Box sx={styles.infoRow}>
                                <Typography sx={styles.label}>Fuero</Typography>
                                <Typography sx={styles.value}>{radicacion.Fuero}</Typography>
                            </Box>
                        )}
                        {radicacion.Jurisdiccion && (
                            <Box sx={styles.infoRow}>
                                <Typography sx={styles.label}>Jurisdicción</Typography>
                                <Typography sx={styles.value}>{radicacion.Jurisdiccion}</Typography>
                            </Box>
                        )}
                    </AccordionDetails>
                </Accordion>
            )}

            {/* Ultimos Movimientos */}
            <Accordion sx={styles.accordion} defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={styles.accordionSummary}>
                    <Typography sx={styles.sectionTitle}>
                        <HistoryIcon fontSize="small" color="primary" /> Últimos Movimientos ({movimientos.length})
                    </Typography>
                </AccordionSummary>
                <AccordionDetails style={{ padding: 0 }}>
                    <List dense disablePadding>
                        {movimientos.length === 0 ? (
                            <ListItem><ListItemText secondary="Sin movimientos recientes" /></ListItem>
                        ) : movimientos.map((m, i) => (
                            <ListItem key={i} sx={styles.movItem} divider={i < movimientos.length - 1}>
                                <ListItemText
                                    primary={
                                        <Typography variant="body2" style={{ fontSize: '0.8rem' }}>
                                            {m.Tipo || 'Movimiento'} {m.Descripcion ? `- ${m.Descripcion}` : ''}
                                        </Typography>
                                    }
                                    secondary={
                                        <Typography variant="caption" color="text.secondary">
                                            {m.Fecha || '-'}
                                        </Typography>
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default ContextoExpediente;
